/**
 * World palette, render layer offsets and lighting.
 *
 * Every value here is a flat, unlit colour: the world reads as pixel-art
 * because the ground, water, runways and paths use basic materials while only
 * trees, buildings and aircraft receive lambert shading.
 */

export type RunwayColor = "yellow" | "blue" | "red";

export interface RunwayPalette {
  readonly base: number;
  readonly center: number;
  readonly marker: number;
}

export const PALETTE = Object.freeze({
  background: 0x78b67a,
  ground: 0x78b67a,
  scenery: Object.freeze({
    grassLight: 0x86bf80,
    grassMid: 0x70ad73,
    grassDark: 0x659d69,
    grassTuft: 0x4f8d5d,
    dirt: 0xe8e0d1,
    water: 0x48abc1,
    streamBank: 0xd5e8bc,
    treeTrunk: 0x795c3d,
    treeLight: 0x5ca96c,
    treeDark: 0x397e53,
    building: 0xd8c5a3,
    buildingRoof: 0xb99e77,
    buildingDark: 0x756c5f,
    window: 0x547d88,
    shadow: 0x416d50,
    /** Shimmer bands riding down the stream. */
    waterHighlight: 0x8fd8e8,
    /** Cloud shadows drifting over the ground, and the birds crossing it. */
    cloudShadow: 0x5f9c68,
    bird: 0x3c5a4a,
    windsockPole: 0xd8d4c8,
    windsockLight: 0xf4f1e6,
    windsockDark: 0xe8833a,
  }),
  runway: Object.freeze({
    yellow: Object.freeze({
      base: 0xa16207,
      center: 0xfef3c7,
      marker: 0xfacc15,
    }),
    blue: Object.freeze({ base: 0x315c9f, center: 0xb6d2ff, marker: 0x3b82f6 }),
    red: Object.freeze({ base: 0x9b3337, center: 0xffb5b7, marker: 0xf05252 }),
  }) as Readonly<Record<RunwayColor, RunwayPalette>>,
  path: 0x274465,
  pathActive: 0x0d2f5f,
  crash: 0xf05252,
  warning: 0xffffff,
  /** Anti-collision light on a parked aircraft. */
  beacon: 0xffd166,
  outboundAircraft: 0xa7afb3,
  outboundAircraftDark: 0x515b62,
  aircraftShadow: 0x496175,
  aircraftDark: 0x21384c,
  rejectMix: 0x5b0b15,
  sailboat: Object.freeze({
    hull: 0x8d4e31,
    deck: 0xf4e5c5,
    mast: 0x604532,
    sail: 0xfff8de,
    wake: 0xd8f5f1,
  }),
});

/** Z offsets that stack the flat world without depth fighting. */
export const LAYER = Object.freeze({
  ground: -0.12,
  terrain: -0.1,
  stream: -0.07,
  dirt: -0.055,
  sceneryShadow: -0.03,
  runway: 0,
  path: 0.18,
  aircraft: 2.4,
  halo: 0.08,
});

export const LIGHTING = Object.freeze({
  ambient: Object.freeze({ color: 0xf6fbff, intensity: 1.45 }),
  sun: Object.freeze({
    color: 0xfff4d7,
    intensity: 1.85,
    position: Object.freeze({ x: -45, y: -60, z: 110 }),
  }),
});

/** HUD colours (the panels are drawn as DOM, not in the scene). */
export const UI = Object.freeze({
  panel: "#121c2a",
  panelText: "#f3f6f8",
  panelBorder: "rgb(242 246 249 / 0.18)",
  panelDivider: "rgb(242 246 249 / 0.28)",
  yellow: "#facc15",
  blue: "#3b82f6",
  red: "#f05252",
  accent: "#1b5b88",
  warning: "#f05252",
});

export function colorToCss(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

/** Linear blend between two packed 0xRRGGBB colours. */
export function mixColor(from: number, to: number, amount: number): number {
  const fromR = (from >> 16) & 255;
  const fromG = (from >> 8) & 255;
  const fromB = from & 255;
  const toR = (to >> 16) & 255;
  const toG = (to >> 8) & 255;
  const toB = to & 255;
  return (
    (Math.round(fromR + (toR - fromR) * amount) << 16) |
    (Math.round(fromG + (toG - fromG) * amount) << 8) |
    Math.round(fromB + (toB - fromB) * amount)
  );
}
